/** Theme tokens for fullscreen play event displays (public board + kiosk). */
export const playEventDisplayTheme = {
  shell:
    'flex h-dvh flex-col overflow-hidden bg-gray-50 text-gray-900 dark:bg-[#0b0d12] dark:text-white',
  header:
    'shrink-0 border-b border-gray-200 px-4 py-4 sm:px-8 sm:py-5 dark:border-white/10',
  accentLabel:
    'text-xs font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-400/90',
  accentText: 'text-primary-700 dark:text-primary-400',
  accentIconWrap:
    'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary-100 p-1 dark:bg-primary-500/15',
  accentIcon: 'h-6 w-6 text-primary-700 dark:text-primary-400',
  muted: 'text-sm text-gray-600 dark:text-white/55',
  mutedSubtle: 'text-xs text-gray-500 dark:text-white/40',
  mutedFaint: 'text-sm text-gray-500 dark:text-white/45',
  badge:
    'rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-white/10 dark:text-white/80',
  accentBadge:
    'rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-500/15 dark:text-primary-300',
  main: 'mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col overflow-hidden p-4 sm:p-6',
  section:
    'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-white/10 dark:bg-white/[0.03]',
  sectionHeading:
    'text-sm font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-400',
  spinner:
    'h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600 dark:border-white/20 dark:border-t-primary-400',
  bodyText: 'max-w-md text-lg text-gray-700 dark:text-white/70',
  finishedSection:
    'flex min-h-0 flex-1 flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center sm:px-10 dark:border-white/10 dark:bg-white/[0.03]',
  finishedIconWrap:
    'mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-500/10',
  finishedIcon: 'h-10 w-10 text-primary-700 dark:text-primary-400',
  sittingOut:
    'rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm text-gray-600 dark:border-white/10 dark:bg-black/20 dark:text-white/55',
  sittingOutLabel: 'font-medium text-gray-800 dark:text-white/75',
  participantName: 'text-gray-900 dark:text-white',
  primaryButton:
    'bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:text-black dark:hover:bg-primary-400',
  outlineButton:
    'border-gray-300 bg-white text-gray-900 hover:bg-gray-50 dark:border-white/20 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:hover:text-white',
  kioskActionButton:
    'h-14 min-h-14 rounded-xl px-8 text-base font-semibold [&_svg]:size-5',
  kioskDialog:
    'gap-6 p-6 sm:max-w-lg sm:p-8 md:max-w-xl [&>button]:right-5 [&>button]:top-5 [&>button]:h-11 [&>button]:w-11 [&>button_svg]:h-5 [&>button_svg]:w-5',
  kioskDialogTitle: 'text-2xl font-bold tracking-tight',
  kioskDialogDescription: 'text-base leading-relaxed',
  kioskField:
    'h-12 rounded-xl border-gray-300 bg-white px-4 text-base text-gray-900 placeholder:text-base dark:border-white/10 dark:bg-black/20 dark:text-white',
  kioskFieldWithIcon: 'pl-12',
  kioskSearchIcon:
    'pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500 dark:text-white/40',
  kioskAction:
    'h-12 min-h-12 rounded-xl px-6 text-base font-semibold',
  kioskResultRow:
    'flex min-h-[3.5rem] w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 text-left text-base transition-colors hover:bg-gray-100 active:scale-[0.99] disabled:opacity-50 dark:border-white/10 dark:bg-black/25 dark:hover:bg-white/10',
  kioskSegmentList:
    'grid grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-gray-100 p-1.5 dark:border-white/10 dark:bg-black/20',
  kioskSegmentButton:
    'min-h-12 rounded-lg px-4 text-base font-medium transition-colors touch-manipulation',
  kioskSectionLabel:
    'text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-white/45',
  kioskBodySpacing: 'space-y-4',
  kioskResultsList:
    'max-h-[min(24rem,42vh)] space-y-4 overflow-y-auto overscroll-contain pr-1',
  kioskHint: 'text-base text-gray-500 dark:text-white/45',
  kioskPanel:
    'rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 dark:border-white/10 dark:bg-black/20',
  iconButton:
    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:border-white/10 dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white',
  dialog:
    'max-h-[90vh] overflow-y-auto border-gray-200 bg-white text-gray-900 dark:border-white/10 dark:bg-[#12151c] dark:text-white',
  dialogDescription: 'text-gray-600 dark:text-white/55',
  input:
    'border-gray-300 bg-white text-gray-900 dark:border-white/10 dark:bg-black/20 dark:text-white',
  panel:
    'rounded-lg border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-black/20',
  listItem:
    'flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-white/10 dark:bg-black/20',
  tabList: 'flex rounded-lg border border-gray-200 bg-gray-100 p-1 dark:border-white/10 dark:bg-black/20',
  tabActive: 'bg-white text-gray-900 dark:bg-white/10 dark:text-white',
  tabInactive:
    'text-gray-600 hover:text-gray-900 dark:text-white/55 dark:hover:text-white/80',
  divider: 'border-t border-gray-200 dark:border-white/10',
} as const;
